import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Building2, Car, Plane, GraduationCap, Map, Building, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const programIcons = {
  greenstay: Building2,
  greentrack: Car,
  flygreen: Plane,
  greencampus: GraduationCap,
  greentourism: Map,
  greenbuild: Building,
  greenpaper: FileText,
};

const programColors = {
  greenstay: "from-emerald-500 to-emerald-600",
  greentrack: "from-green-500 to-green-600",
  flygreen: "from-sky-500 to-sky-600",
  greencampus: "from-teal-500 to-teal-600",
  greentourism: "from-lime-500 to-lime-600",
  greenbuild: "from-slate-500 to-slate-600",
  greenpaper: "from-amber-500 to-amber-600",
};

const dynamicFields = {
  greenstay: { label: "Number of Nights", placeholder: "e.g., 3" },
  greentrack: { label: "Distance (km)", placeholder: "e.g., 500" },
  flygreen: { label: "Number of Flights", placeholder: "e.g., 2" },
  greencampus: { label: "Number of Students", placeholder: "e.g., 1000" },
  greentourism: { label: "Number of Trips", placeholder: "e.g., 5" },
  greenbuild: { label: "Square Feet", placeholder: "e.g., 5000" },
  greenpaper: { label: "Reams of Paper", placeholder: "e.g., 100" },
};

const suggestedAmounts = [500, 1000, 2500, 5000, 10000];

export default function GreenProgramPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [program, setProgram] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    mobile: "",
    email: "",
    amount: "",
    dynamic_value: ""
  });

  useEffect(() => {
    const fetchProgram = async () => {
      try {
        const response = await axios.get(`${API}/programs/${slug}`);
        setProgram(response.data);
      } catch (error) {
        console.error("Error fetching program:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchProgram();
  }, [slug]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.amount || parseInt(formData.amount) < 100) {
      toast.error("Minimum contribution amount is ₹100");
      return;
    }
    
    setSubmitting(true);
    
    try {
      const response = await axios.post(`${API}/contributions`, {
        name: formData.name,
        mobile: formData.mobile,
        email: formData.email,
        amount: parseInt(formData.amount),
        program_id: program.id,
        dynamic_field: dynamicFields[slug]?.label,
        dynamic_value: formData.dynamic_value
      });
      
      toast.success("Contribution successful! Redirecting...");
      
      // Mock payment success - redirect to success page
      setTimeout(() => {
        navigate(`/payment-success?type=contribution&id=${response.data.id}&certificate=${response.data.certificate_id}`);
      }, 1500);
    } catch (error) {
      console.error("Contribution error:", error);
      toast.error("Contribution failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 py-16">
          <div className="animate-pulse space-y-8">
            <div className="h-12 bg-slate-200 rounded w-1/3" />
            <div className="h-64 bg-slate-100 rounded-2xl" />
          </div>
        </div>
      </div>
    );
  }

  if (!program) {
    return (
      <div className="min-h-screen pt-20 bg-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-900 mb-4">Program not found</h1>
          <Link to="/">
            <Button>Back to Home</Button>
          </Link>
        </div>
      </div>
    );
  }

  const IconComponent = programIcons[slug] || Building2;
  const colorClass = programColors[slug] || "from-green-500 to-green-600";
  const dynamicField = dynamicFields[slug];

  return (
    <div className="min-h-screen pt-20 bg-white" data-testid="green-program-page">
      {/* Hero */}
      <section className={`py-16 lg:py-20 bg-gradient-to-br ${colorClass} text-white`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/" className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-6">
            <ArrowLeft className="h-4 w-4" />
            Back to Home
          </Link>
          <div className="flex items-center gap-4 mb-4">
            <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
              <IconComponent className="h-8 w-8" />
            </div>
            <h1 className="font-montserrat text-4xl sm:text-5xl font-bold" data-testid="program-title">
              {program.name}
            </h1>
          </div>
          <p className="text-xl text-white/90 max-w-2xl">
            {program.description}
          </p>
        </div>
      </section>

      {/* Form */}
      <section className="py-16 lg:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Info */}
            <div>
              <h2 className="font-montserrat text-2xl font-bold text-slate-900 mb-4">
                Make Your Contribution
              </h2>
              <p className="text-slate-600 mb-6 leading-relaxed">
                Your contribution will be used to support verified carbon offset projects. 
                After payment, you'll receive a digital certificate with your name, 
                contribution amount, and CO₂ offset details.
              </p>
              <div className="bg-forest-50 rounded-2xl p-6">
                <h3 className="font-semibold text-forest-900 mb-3">How it works:</h3>
                <ol className="space-y-2 text-sm text-forest-800">
                  <li className="flex items-start gap-2">
                    <span className="w-5 h-5 bg-forest-200 rounded-full flex items-center justify-center text-xs font-bold">1</span>
                    Fill in your details and contribution amount
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-5 h-5 bg-forest-200 rounded-full flex items-center justify-center text-xs font-bold">2</span>
                    Complete payment via UPI, card, or net banking
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-5 h-5 bg-forest-200 rounded-full flex items-center justify-center text-xs font-bold">3</span>
                    Receive your carbon contribution certificate
                  </li>
                </ol>
              </div>
            </div>

            {/* Form */}
            <Card>
              <CardContent className="p-6">
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="name">Full Name *</Label>
                    <Input
                      id="name"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Enter your name"
                      data-testid="contribution-name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="mobile">Mobile Number *</Label>
                    <Input
                      id="mobile"
                      required
                      value={formData.mobile}
                      onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                      placeholder="Enter mobile number"
                      data-testid="contribution-mobile"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="Enter email address"
                      data-testid="contribution-email"
                    />
                  </div>
                  {dynamicField && (
                    <div>
                      <Label htmlFor="dynamic_value">{dynamicField.label}</Label>
                      <Input
                        id="dynamic_value"
                        value={formData.dynamic_value}
                        onChange={(e) => setFormData({ ...formData, dynamic_value: e.target.value })}
                        placeholder={dynamicField.placeholder}
                        data-testid="contribution-dynamic"
                      />
                    </div>
                  )}
                  <div>
                    <Label htmlFor="amount">Contribution Amount (₹) *</Label>
                    <Input
                      id="amount"
                      type="number"
                      required
                      min="100"
                      value={formData.amount}
                      onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                      placeholder="Enter amount (min ₹100)"
                      data-testid="contribution-amount"
                    />
                    <div className="flex flex-wrap gap-2 mt-2">
                      {suggestedAmounts.map((amount) => (
                        <button
                          key={amount}
                          type="button"
                          onClick={() => setFormData({ ...formData, amount: amount.toString() })}
                          className={`px-3 py-1 text-sm rounded-full border transition-colors ${
                            formData.amount === amount.toString()
                              ? "bg-forest-950 text-white border-forest-950"
                              : "border-slate-200 hover:border-forest-500"
                          }`}
                        >
                          ₹{amount.toLocaleString()}
                        </button>
                      ))}
                    </div>
                  </div>
                  {formData.amount && parseInt(formData.amount) >= 100 && (
                    <div className="bg-forest-50 rounded-xl p-4 text-center">
                      <p className="text-sm text-forest-700">Estimated CO₂ Offset</p>
                      <p className="text-2xl font-bold text-forest-900">
                        {((parseInt(formData.amount) / 100) * 10).toLocaleString()} kg
                      </p>
                    </div>
                  )}
                  <Button
                    type="submit"
                    className="w-full bg-forest-950 hover:bg-forest-900"
                    size="lg"
                    disabled={submitting}
                    data-testid="submit-contribution"
                  >
                    {submitting ? "Processing..." : "Proceed to Payment"}
                  </Button>
                  <p className="text-xs text-slate-500 text-center">
                    By contributing, you agree to our terms and conditions.
                  </p>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
}
